<script setup>
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { reactive, watch, ref } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import wootColorPicker from 'dashboard/components/widgets/ColorPicker.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  selectedRecord: {
    type: Object,
    default: () => ({}),
  },
  isEdit: {
    type: Boolean,
    default: false,
  },
  pipelineId: {
    type: [String, Number],
    required: true,
  },
});
const emit = defineEmits(['close']);
const store = useStore();
const { t } = useI18n();

const formData = reactive({
  name: '',
  color: '#1BFBBD',
  position: 1,
  stage_type: 'active',
});

const isSubmitting = ref(false);

const rules = {
  name: { required },
  position: { required },
  stage_type: { required },
};

const v$ = useVuelidate(rules, formData);

watch(
  () => props.selectedRecord,
  newRecord => {
    if (props.isEdit && newRecord) {
      formData.name = newRecord.name;
      formData.color = newRecord.color || '#1BFBBD';
      formData.position = newRecord.position;
      formData.stage_type = newRecord.stage_type || 'active';
    }
  },
  { immediate: true, deep: true }
);

const onColorChange = color => {
  formData.color = color.hex;
};

const submitForm = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;
  if (!props.pipelineId) {
    useAlert(t('COMPONENTS.CODE_MESSAGE.ERROR'));
    return;
  }

  isSubmitting.value = true;
  try {
    const action = props.isEdit
      ? 'pipelineStages/update'
      : 'pipelineStages/create';
    const payload = props.isEdit
      ? {
          pipelineId: props.pipelineId,
          id: props.selectedRecord.id,
          ...formData,
        }
      : { pipelineId: props.pipelineId, stageData: formData };
    await store.dispatch(action, payload);
    useAlert(
      props.isEdit ? t('CRM.FORM.SUCCESS_UPDATE') : t('CRM.FORM.SUCCESS_CREATE')
    );
    emit('close');
  } catch (error) {
    useAlert(error?.message || t('COMPONENTS.CODE_MESSAGE.ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <div class="h-auto overflow-auto flex flex-col pt-8">
    <woot-modal-header
      :header-title="
        isEdit ? $t('CRM.FORM.TITLE_UPDATE') : $t('CRM.FORM.TITLE_CREATE')
      "
    />
    <form class="flex flex-col w-full" @submit.prevent="submitForm">
      <div class="w-full px-8 pb-8">
        <label>
          {{ $t('CRM.FORM.NAME') }}
          <input
            v-model.trim="formData.name"
            type="text"
            :placeholder="$t('CRM.FORM.NAME_PLACEHOLDER')"
            @input="v$.name.$touch"
          />
          <span v-if="v$.name.$error" class="message text-n-ruby-11">
            {{ $t('CRM.FORM.NAME_REQUIRED') }}
          </span>
        </label>

        <label>
          {{ $t('CRM.FORM.TYPE') }}
          <select v-model="formData.stage_type" class="w-full mt-2">
            <option value="active">{{ $t('CRM.FORM.TYPES.ACTIVE') }}</option>
            <option value="won">{{ $t('CRM.FORM.TYPES.WON') }}</option>
            <option value="lost">{{ $t('CRM.FORM.TYPES.LOST') }}</option>
          </select>
        </label>

        <label class="mt-4">
          {{ $t('CRM.FORM.POSITION') }}
          <input
            v-model.number="formData.position"
            type="number"
            min="1"
            :placeholder="$t('CRM.FORM.POSITION_PLACEHOLDER')"
          />
        </label>

        <label class="mt-4">
          {{ $t('CRM.FORM.COLOR') }}
          <div class="flex items-center gap-4 mt-2">
            <wootColorPicker :color="formData.color" @change="onColorChange" />
            <span class="text-sm border border-n-weak px-2 py-1 rounded">{{
              formData.color
            }}</span>
          </div>
        </label>

        <div class="flex justify-end gap-2 mt-8">
          <Button variant="ghost" type="button" @click.prevent="$emit('close')">
            {{ $t('CRM.FORM.CANCEL') }}
          </Button>
          <Button :is-loading="isSubmitting" type="submit">
            {{
              isEdit
                ? $t('CRM.FORM.SUBMIT_UPDATE')
                : $t('CRM.FORM.SUBMIT_CREATE')
            }}
          </Button>
        </div>
      </div>
    </form>
  </div>
</template>
